import type { Feature } from "geojson";

import { isAnyLineString, isAnyPolygon, isPoint } from "fond/types/geojson";

import type { DemandPoint, ParcelLayerItem, StreetLayerItem } from "./layer";

export interface LayerClassificationInputs {
  parcels: ParcelLayerItem[];
  streets: StreetLayerItem[];
  demands: DemandPoint[];

  /** The original parcel features, keyed by the ids handed to the model. */
  parcelFeatureById: Map<number, Feature>;

  /** The original demand features, keyed by the ids handed to the model. */
  demandFeatureById: Map<number | string, Feature>;
}

/**
 * Build classification inputs from layer features.
 */
export function buildLayerClassificationInputs({
  parcelFeatures,
  streetFeatures,
  demandFeatures,
}: {
  parcelFeatures: Feature[];
  streetFeatures: Feature[];
  demandFeatures: Feature[];
}): LayerClassificationInputs {
  const parcelFeatureById = new Map<number, Feature>();
  const parcels: ParcelLayerItem[] = parcelFeatures.filter(isAnyPolygon).map((feature, index) => {
    const properties = feature.properties as Record<string, unknown> | null;
    parcelFeatureById.set(index, feature);
    return {
      id: index,
      geometry: feature.geometry,
      addressType: (properties?.AddressType as string | undefined) ?? null,
    };
  });

  const streets: StreetLayerItem[] = streetFeatures.filter(isAnyLineString).map((feature) => {
    const properties = feature.properties as Record<string, unknown> | null;
    // The street attribute is `Highway` in area-select data and `highway` in raw OSM data
    const highway = (properties?.highway ?? properties?.Highway) as string | undefined;
    return { geometry: feature.geometry, highway: highway ?? null };
  });

  const demandFeatureById = new Map<number | string, Feature>();
  const demands: DemandPoint[] = demandFeatures.filter(isPoint).map((feature, index) => {
    const id = feature.id ?? `demand-${index}`;
    demandFeatureById.set(id, feature);
    return { id, geometry: feature.geometry };
  });

  return { parcels, streets, demands, parcelFeatureById, demandFeatureById };
}
